import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { Trans, useTranslation } from 'react-i18next';
import { api, ApiError } from '../../lib/api';
import { useAuthStore, type UserRole } from '../../stores/auth.store';
import { roleFullLabel } from '../../lib/roles';
import { AppStoreBadge, GooglePlayBadge } from '../../components/StoreBadges';

// Smart invite link (founder 2026-06-22): davet linki cihaza göre yönlendirir.
// iOS'ta uygulama YÜKLÜYSE universal link (AASA + applinks:admin.custel.com.tr) bu
// sayfayı HİÇ açmadan app'i açar; bu sayfaya düşmek = app yüklü değil → indirme.
// Android henüz hazır değil → "yakında". Masaüstü → web şifre-belirleme akışı.
type Platform = 'ios' | 'android' | 'desktop';

function detectPlatform(): Platform {
  if (typeof navigator === 'undefined') return 'desktop';
  const ua = navigator.userAgent || '';
  // iPadOS 13+ Safari masaüstü UA verir ama dokunmatik → iPad'i de yakala.
  const isIOS = /iPhone|iPod|iPad/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
  if (isIOS) return 'ios';
  if (/Android/.test(ua)) return 'android';
  return 'desktop';
}

// Store linkleri (deploy env'inden). Boşsa badge "yakında" (soluk) gösterilir.
// custel-admin-update.sh içinde VITE_* olarak set edilir.
const IOS_APP_STORE_URL = (import.meta.env.VITE_IOS_APP_STORE_URL as string | undefined) ?? '';
const IOS_TESTFLIGHT_URL = (import.meta.env.VITE_IOS_TESTFLIGHT_URL as string | undefined) ?? '';
const ANDROID_STORE_URL = (import.meta.env.VITE_ANDROID_STORE_URL as string | undefined) ?? '';
// App Store yayınlanana kadar TestFlight'a düş (review sürecinde link ölü kalmasın).
const APP_STORE_HREF = IOS_APP_STORE_URL || IOS_TESTFLIGHT_URL;

interface InvitationDetails {
  email: string;
  fullName: string;
  role: string;
  department?: string;
  companyName: string;
}

export function AcceptInvitePage() {
  const { t } = useTranslation('auth');
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const fetchSession = useAuthStore((s) => s.fetchSession);
  const [details, setDetails] = useState<InvitationDetails | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const platform = useMemo(detectPlatform, []);
  // Mobil kullanıcı "yine de tarayıcıda devam et" derse web formuna düş.
  const [forceWeb, setForceWeb] = useState(false);

  const schema = useMemo(
    () =>
      z.object({
        fullName: z.string().min(2),
        password: z.string().min(12, t('invite.passwordMin')),
      }),
    [t],
  );

  type Input = z.infer<typeof schema>;

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<Input>({ resolver: zodResolver(schema) });

  useEffect(() => {
    if (!token) return;
    api
      .get<InvitationDetails>(`/invitations/token/${token}`)
      .then((data) => {
        setDetails(data);
        setValue('fullName', data.fullName);
      })
      .catch((err) => {
        const message = err instanceof ApiError ? err.message : t('invite.notFound');
        setError(message);
      });
  }, [token, setValue]);

  async function onSubmit(input: Input) {
    if (!token) return;
    setSubmitting(true);
    try {
      await api.post('/auth/invitations/accept', {
        token,
        password: input.password,
        fullName: input.fullName,
      });
      await fetchSession();
      toast.success(t('invite.accountCreated'));
      navigate('/');
    } catch (err) {
      const message = err instanceof ApiError ? err.message : t('invite.acceptFailed');
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  }

  if (error) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-custel-bg-subtle p-8">
        <div className="w-full max-w-md rounded-custel border border-custel-danger bg-custel-danger-subtle p-6">
          <h1 className="mb-2 text-lg font-semibold text-custel-danger-fg">{t('invite.invalidTitle')}</h1>
          <p className="text-sm text-custel-danger-fg">{error}</p>
          <Link
            to="/giris"
            className="mt-4 inline-block text-sm font-medium text-custel-ink underline underline-offset-2 hover:text-custel-g700"
          >
            {t('invite.backToLogin')}
          </Link>
        </div>
      </main>
    );
  }

  if (!details) {
    return (
      <main className="flex min-h-screen items-center justify-center">
        <p className="text-sm text-custel-text-secondary">{t('invite.checking')}</p>
      </main>
    );
  }

  // Mobil cihaz: uygulamayı indir (iOS app yüklüyse universal link buraya hiç düşmezdi).
  if ((platform === 'ios' || platform === 'android') && !forceWeb) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-custel-bg-subtle p-8">
        <div className="w-full max-w-md rounded-custel border border-custel-border bg-custel-surface p-8 text-center shadow-sm">
          <h1 className="mb-2 text-2xl font-semibold text-custel-text">{t('invite.downloadTitle')}</h1>
          <p className="mb-6 text-sm text-custel-text-secondary">
            <Trans
              t={t}
              i18nKey="invite.downloadDescription"
              values={{ company: details.companyName, role: roleFullLabel(details.role as UserRole) }}
              components={{ b: <strong /> }}
            />
          </p>

          <div className="mx-auto flex max-w-xs items-stretch justify-center gap-2">
            <AppStoreBadge href={APP_STORE_HREF || undefined} />
            <GooglePlayBadge href={ANDROID_STORE_URL || undefined} />
          </div>

          <button
            type="button"
            onClick={() => setForceWeb(true)}
            className="mt-6 text-sm font-medium text-custel-text-secondary underline underline-offset-2 hover:text-custel-ink"
          >
            {t('invite.continueInBrowser')}
          </button>
        </div>
      </main>
    );
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-custel-bg-subtle p-8">
      <div className="w-full max-w-md rounded-custel border border-custel-border bg-custel-surface p-8 shadow-sm">
        <h1 className="mb-2 text-2xl font-semibold text-custel-text">{t('invite.title')}</h1>
        <p className="mb-6 text-sm text-custel-text-secondary">
          <Trans
            t={t}
            i18nKey="invite.intro"
            values={{ company: details.companyName, role: roleFullLabel(details.role as UserRole) }}
            components={{ b: <strong /> }}
          />
        </p>

        <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
          <div>
            <label className="mb-1 block text-sm font-medium">{t('invite.emailLabel')}</label>
            <input type="email" value={details.email} disabled className="input-base bg-custel-bg-subtle" />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-custel-text" htmlFor="fullName">
              {t('invite.fullNameLabel')}
            </label>
            <input id="fullName" type="text" className="input-base" {...register('fullName')} />
            {errors.fullName && <p className="mt-1 text-xs text-custel-danger">{errors.fullName.message}</p>}
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-custel-text" htmlFor="password">
              {t('invite.passwordLabel')} <span className="text-custel-text-tertiary">{t('invite.passwordHint')}</span>
            </label>
            <input
              id="password"
              type="password"
              className="input-base"
              autoComplete="new-password"
              {...register('password')}
            />
            {errors.password && <p className="mt-1 text-xs text-custel-danger">{errors.password.message}</p>}
          </div>

          <button type="submit" className="btn-primary w-full" disabled={submitting}>
            {submitting ? t('invite.creatingAccount') : t('invite.accept')}
          </button>
        </form>
      </div>
    </main>
  );
}
